import { api } from '../client'
import type { ReplaceCheckResult } from './files.api'

export type JobStatus = 'uploading' | 'queued' | 'parsing' | 'loading' | 'publishing' | 'done' | 'failed' | 'aborted'

export type JobType =
  | 'publish_csv'
  | 'publish_geojson'
  | 'publish_shapefile'
  | 'publish_geopackage'
  | 'publish_gdb'
  | 'csv_table_import'
  | 'upload_geocoding_cache'

export interface UploadInitRequest {
  filename: string
  size: number
  contentType: string
  type: JobType
  input: Record<string, unknown>
}

export interface UploadPartUrl {
  partNumber: number
  url: string
}

export interface UploadInitResponse {
  jobId: string
  uploadId: string
  bucket: string
  key: string
  partSize: number
  partCount: number
  urls: UploadPartUrl[]
  expiresAt: string
}

export interface UploadCompletePart {
  partNumber: number
  etag: string
}

export interface UploadCompleteRequest {
  parts: UploadCompletePart[]
}

export interface JobResult {
  layerId?: string
  featureCount?: number
  extent?: [number, number, number, number]
  /** A replace CHECK's findings (#1547): set on a `done` job that ran with `input.check`. */
  check?: ReplaceCheckResult
}

/**
 * A job's error as the jobs API returns it: only the `code`, `message` and `details` of the error
 * the worker stored (libs/mongodb Job.schema.ts), never its stack (an allowlist,
 * services/console/jobs public-job.ts). The one console definition.
 */
export interface JobError {
  code: string
  message: string
  /** Structured context for the code, e.g. the column diff behind SCHEMA_CHANGE_REQUIRES_CONFIRMATION. */
  details?: Record<string, unknown>
}

export interface JobDoc {
  _id: string
  tenantId?: string
  createdBy: string
  ownedBy: string
  type: JobType
  status: JobStatus
  stage?: string
  percentComplete: number
  bytesTotal: number
  bytesProcessed: number
  rowsTotal?: number
  rowsProcessed: number
  s3Bucket: string
  s3Key: string
  s3UploadId?: string
  input: Record<string, unknown>
  result?: JobResult
  error?: JobError
  startedAt?: string
  finishedAt?: string
  createdAt: string
  updatedAt: string
}

export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = ['done', 'failed', 'aborted'] as const

export function isTerminalJobStatus(status: JobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status)
}

export const uploadsApi = {
  init: (req: UploadInitRequest) => api.post<UploadInitResponse>('/uploads/init', req),

  complete: (jobId: string, req: UploadCompleteRequest) => api.post<JobDoc>(`/uploads/${jobId}/complete`, req),

  abort: (jobId: string) => api.post<JobDoc>(`/uploads/${jobId}/abort`, {}),
}
